import React, { useState } from "react";
import { Button, Modal, message, FormInstance, Typography } from "antd";
import { SyncOutlined, WarningOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useCustom, useApiUrl } from "@refinedev/core";

interface SyncAnalyticProps {
    form: FormInstance<any>;
}

interface AnalyticEvent {
    event_name: string;
    event_params: {
        param_key: string;
        param_type: string;
    }[];
}

const { Text } = Typography;

export const SyncAnalytic: React.FC<SyncAnalyticProps> = ({ form }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const apiUrl = useApiUrl("nestjsx");
    const productCode = form.getFieldValue("code") || "";

    const { refetch: syncAnalyticEvents, isFetching } = useCustom({
        dataProviderName: "nestjsx",
        method: "get",
        url: `${apiUrl}/proxy/v2/event_tracking/${productCode}`,
        queryOptions: {
            enabled: false,
        },
    });

    const handleOpenModal = () => {
        setIsModalVisible(true);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
    };

    const handleSync = async () => {
        const currentProductCode = form.getFieldValue("code");

        if (!currentProductCode) {
            message.error("Product code is required for syncing analytic events");
            return;
        }

        try {
            const { data } = await syncAnalyticEvents();
            const apiAnalyticEvents: AnalyticEvent[] = (data as any)?.data?.analytic_events || [];
            if (!apiAnalyticEvents.length) {
                message.warning("No analytic events found for this product");
                return;
            }

            // Convert API data to form format
            const formAnalyticEvents = apiAnalyticEvents.map((event) => ({
                event_name: event.event_name,
                event_params: event.event_params || [],
                is_new: false,
                is_editing: false,
            }));

            // Get current analytic events count for comparison
            const currentEvents = form.getFieldValue("analytic_events") || [];

            // Replace all current analytic events with new data
            form.setFieldValue("analytic_events", formAnalyticEvents);

            message.success({
                content: (
                    <div>
                        <CheckCircleOutlined className="text-green-500 mr-2" />
                        Successfully synced {formAnalyticEvents.length} analytic events
                        {currentEvents.length > 0 && (
                            <div className="text-xs text-gray-500 mt-1">
                                Replaced {currentEvents.length} existing events
                            </div>
                        )}
                    </div>
                ),
                duration: 4,
            });

            handleCloseModal();
        } catch (error) {
            console.error("Sync error:", error);
            message.error("Failed to sync analytic events. Please try again.");
        }
    };

    return (
        <>
            <Button type="default" icon={<SyncOutlined />} onClick={handleOpenModal} className="mb-3" size="small">
                Sync Analytics
            </Button>

            <Modal
                title={
                    <div className="flex items-center space-x-2">
                        <WarningOutlined className="text-orange-500" />
                        <span>Sync Analytic Events for product code </span>
                        <Text code>{form.getFieldValue("code") || "Not specified"}</Text>
                    </div>
                }
                open={isModalVisible}
                onCancel={handleCloseModal}
                footer={[
                    <Button key="cancel" onClick={handleCloseModal} disabled={isFetching}>
                        Cancel
                    </Button>,
                    <Button
                        key="sync"
                        type="primary"
                        onClick={handleSync}
                        loading={isFetching}
                        icon={<SyncOutlined />}
                        danger
                    >
                        {isFetching ? "Syncing..." : "Sync Analytics"}
                    </Button>,
                ]}
                width={500}
                destroyOnClose
                closable={!isFetching}
                maskClosable={!isFetching}
            >
                <div className="space-y-4">
                    {/* Warning message */}
                    <div className="bg-orange-50 border border-orange-200 rounded-md p-4 flex items-start space-x-3">
                        <WarningOutlined className="text-orange-500 mt-0.5 text-lg" />
                        <div className="text-sm">
                            <Text strong className="text-orange-700 block mb-2">
                                Warning: This action cannot be undone!
                            </Text>
                            <div className="text-orange-600 space-y-1">
                                <div>• This will replace ALL current analytic events</div>
                                <div>• New events will be fetched from event tracking service</div>
                                <div>• Make sure to save your form after syncing</div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};
